/**
 * useWatchPointAutosave
 *
 * Shared resume-point autosave cadence for WatchPage, SeriesWatchPage and
 * VideoPlayer. Those three each drove their own setInterval and duplicated
 * the same rules with drifting constants (5s vs 30s) and no live gate, which
 * at scale multiplies into a write (plus a CORS preflight) per viewer every
 * few seconds — including for content that is currently live, where a resume
 * point is meaningless.
 *
 * Rules enforced here:
 *   - Save on a fixed 30s cadence (WATCH_POINT_SAVE_INTERVAL_MS), never while
 *     `isLive` is true.
 *   - Save immediately (bypassing the interval) when the tab is hidden or the
 *     page is being navigated away from/closed, so a save isn't lost to the
 *     next tick. These go out with `keepalive: true` so the request survives
 *     page teardown.
 *   - Never save the same (rounded) second twice in a row.
 *
 * Pause/ended saves stay in each caller (the event source differs: a native
 * `<video>` vs a video.js player instance) but should call the `saveNow`
 * this hook returns, so all save paths share one dedupe.
 */
import { useCallback, useEffect, useRef } from 'react';

export const WATCH_POINT_SAVE_INTERVAL_MS = 30000;

/**
 * @param {Object} params
 * @param {boolean} params.enabled - Whether saving should run at all (authed, ids ready).
 * @param {boolean} params.isLive - Skip every save while true.
 * @param {() => number} params.getCurrentTime - Reads the latest playback position.
 * @param {(time: number, opts?: {keepalive?: boolean}) => void} params.onSave - Performs the actual save.
 */
export function useWatchPointAutosave({ enabled, isLive, getCurrentTime, onSave }) {
  const lastSavedRef = useRef(0);
  const latest = useRef({ enabled, isLive, getCurrentTime, onSave });
  latest.current = { enabled, isLive, getCurrentTime, onSave };

  const saveNow = useCallback((time, opts) => {
    const { enabled: isEnabled, isLive: live, onSave: save } = latest.current;
    if (!isEnabled || live) {
      return;
    }
    const rounded = Math.floor(time);
    if (rounded <= 0 || rounded === lastSavedRef.current) {
      return;
    }
    lastSavedRef.current = rounded;
    save(rounded, opts);
  }, []);

  // Periodic save. `isLive` isn't in the gate here — saveNow is the single
  // enforcement point, so a tick during live content is a cheap no-op rather
  // than a second place this has to be kept in sync.
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      saveNow(latest.current.getCurrentTime());
    }, WATCH_POINT_SAVE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, saveNow]);

  // Immediate save when the tab is backgrounded/closed. keepalive lets the
  // fetch outlive the page (sendBeacon can't carry the auth header this
  // endpoint needs, so this is the documented alternative).
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveNow(latest.current.getCurrentTime(), { keepalive: true });
      }
    };
    const handlePageHide = () => {
      saveNow(latest.current.getCurrentTime(), { keepalive: true });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, saveNow]);

  return saveNow;
}

export default useWatchPointAutosave;
